import { useEffect, useRef } from 'react';

/**
 * Saves and restores scroll position across network interruptions.
 *
 * On connection drop (window.online === false), saves the current scrollY
 * to sessionStorage keyed by the current pathname. On connection restore
 * + data availability, restores the saved position so the user doesn't
 * lose their place in a campaign/lead list.
 *
 * @param routeKey — a stable key for the current route (e.g. campaignId or tab name).
 *   Scroll position is saved per routeKey so navigating between campaigns doesn't
 *   restore the wrong position.
 * @param dataReady — set to true once the route's primary data query has data available.
 *   Scroll is only restored when data is ready to render.
 * @param enabled — when false, skip saving/restoring (e.g. admin campaign board).
 */
export function useScrollRestore(
  routeKey: string,
  dataReady: boolean,
  enabled = true,
) {
  const restoredRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const storageKey = `scroll-pos:${routeKey}`;

    // Save scroll position on disconnect
    const handleOffline = () => {
      try {
        sessionStorage.setItem(storageKey, String(window.scrollY));
      } catch {
        // sessionStorage may be unavailable
      }
    };

    window.addEventListener('offline', handleOffline);

    // Restore scroll position on reconnect + data ready
    if (dataReady && !restoredRef.current) {
      try {
        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
          const pos = parseInt(saved, 10);
          if (!isNaN(pos)) {
            // Use requestAnimationFrame to wait for the DOM to paint with the restored data
            requestAnimationFrame(() => {
              window.scrollTo({ top: pos, behavior: 'instant' as ScrollBehavior });
            });
          }
          sessionStorage.removeItem(storageKey);
        }
      } catch {
        // sessionStorage may be unavailable
      }
      restoredRef.current = true;
    }

    // Reset restored flag when route key changes (user navigated elsewhere)
    if (!dataReady) {
      restoredRef.current = false;
    }

    return () => {
      window.removeEventListener('offline', handleOffline);
    };
  }, [routeKey, dataReady, enabled]);
}